var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Avatar, Badge, ButtonList, CollapseCard, FlexBox, NavigationButton, Tooltip, } from '@dixa/lib-shared-habla';
import { conversationChannelAttributes } from 'lib-shared-legacy-types';
import { useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { useMap } from 'react-use';
import { appActions } from 'app/app.actions';
import { analytics } from 'app/utils/segment';
import { transformUserToApiUser } from 'app/utils/transformUserToApiUser/transformUserToApiUser';
import { validateIfUserCanMerge } from 'app/views/layout/utils/validateIfUserCanMerge/validateIfUserCanMerge';
import { BanUserButtonListItem } from 'components/shared/banUserButtonListItem/banUserButtonListItem';
import { UnbanUserButtonListItem } from 'components/shared/unbanUserButtonListItem/unbanUserButtonListItem';
import { userModalActions } from 'components/usersModal';
import { bansApi } from 'hooks/useBans/api';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { bindActionToPromise } from 'utils/sagaApiGeneratorHelpers';
import { twilioActions } from '../../../../../app/middleware/twilio/actions/twilioActions';
import { numberFormatterInternational } from '../../../../../app/utils/telephonyHelpers';
import { composeEmailActions } from '../../../../shared/composeEmail/actions/composeEmailActions';
import { ContactCardLine } from './contactCardLine';
import '../../../styles/cards.less';
const { getUserBans } = bansApi;
// NOTE: ConversationChannel is also defined in `lib-shared-legacy-types`
// TODO: https://dixa-product.atlassian.net/browse/TEAMS-4919
var ConversationChannel;
(function (ConversationChannel) {
    ConversationChannel["Callback"] = "callback";
    ConversationChannel["ContactForm"] = "contact_form";
    ConversationChannel["Email"] = "email";
    ConversationChannel["FacebookMessenger"] = "facebook_messenger";
    ConversationChannel["Messenger"] = "messenger";
    ConversationChannel["PSTNPhone"] = "pstn_phone";
    ConversationChannel["SMS"] = "sms";
    ConversationChannel["Telegram"] = "telegram";
    ConversationChannel["Twitter"] = "twitter";
    ConversationChannel["Voicemail"] = "voicemail";
    ConversationChannel["WhatsApp"] = "whats_app";
    ConversationChannel["WidgetChat"] = "widget_chat";
})(ConversationChannel || (ConversationChannel = {}));
/**
 * List of available channels that the user is allowed to be banned on through
 * the UI.
 */
const ALLOWED_BANNING_CHANNELS = [
    ConversationChannel.WidgetChat,
];
const ContactCard = (props) => {
    var _a, _b;
    const navigate = useNavigate();
    const { mergeContacts } = useFeatureFlag(['merge-contacts']);
    const [bans, { setAll: setAllBans }] = useMap(new Map([]));
    const getActiveUserBans = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        var _c;
        const userId = (_c = props.user) === null || _c === void 0 ? void 0 : _c.id;
        if (!userId)
            return;
        const { data: { bans }, } = yield getUserBans({
            params: {
                end_user_id: userId,
                active_state: 'active',
            },
        });
        const allowedChannelBans = new Map(ALLOWED_BANNING_CHANNELS.map((channel) => [
            channel,
            bans.find((ban) => ban.channel === channel),
        ]));
        setAllBans(allowedChannelBans);
    }), [setAllBans, (_a = props === null || props === void 0 ? void 0 : props.user) === null || _a === void 0 ? void 0 : _a.id]);
    useEffect(() => {
        getActiveUserBans();
    }, [getActiveUserBans]);
    const handleMakeCall = (e, phoneNumber) => {
        // Prevent default so user phone client is not opened.
        e.preventDefault();
        const { telephonyMakeCall, user, telephonyFromNumber } = props;
        const toNumber = phoneNumber !== null && phoneNumber !== void 0 ? phoneNumber : user === null || user === void 0 ? void 0 : user.phone_number;
        if (!user || !toNumber) {
            return;
        }
        telephonyMakeCall({
            userId: user.id,
            toNumber: toNumber,
            fromNumber: telephonyFromNumber === null || telephonyFromNumber === void 0 ? void 0 : telephonyFromNumber.e164_format,
        });
        analytics.conversationSidebarCustomerInfoPhoneNumberClicked();
    };
    const handleEmailOnClick = (e, email) => {
        // Prevent default so user email client is not opened.
        e.preventDefault();
        const { user } = props;
        if (!user)
            return;
        props.showComposeEmailModal({
            initialValues: {
                toUser: [{ user: Object.assign(Object.assign({}, user), (!!email && { email })) }],
            },
        });
        analytics.conversationSidebarCustomerInfoEmailClicked();
    };
    /** Gets formatted phone number or null in case the user doesn't have a phone. */
    const getPhoneNumberFormat = (userPhoneNumber) => {
        const phoneNumber = userPhoneNumber === null || userPhoneNumber === void 0 ? void 0 : userPhoneNumber.trim();
        if (!phoneNumber)
            return '';
        return phoneNumber.startsWith('+')
            ? numberFormatterInternational({ phoneNumber })
            : phoneNumber;
    };
    /** Gets the avatar element to use. */
    const getAvatarElement = () => {
        const { user = null } = props;
        return _jsx(Avatar, { className: "header-icon", user: user });
    };
    /**
     * Because the backend is handling ban in two different lists the backend two
     * endpoint for ban is not 100% sync, therefor we need to delay the get
     * callback.
     */
    const getActiveUserBansDelayed = () => {
        setTimeout(() => {
            getActiveUserBans();
        }, 2000);
    };
    /** Gets the menu element to use in the dropdown. */
    const getMenuElement = (user) => {
        const { showEditUserModal, conversation, showMergeContactsModal } = props;
        const { initial_channel, id, attributes } = conversation;
        const csid = id ? id : undefined;
        const ipAddress = attributes === null || attributes === void 0 ? void 0 : attributes.ip_address;
        const showBanItem = initial_channel === 'widgetchat';
        const canUserMergeValidation = validateIfUserCanMerge(user);
        return (_jsxs(ButtonList, { children: [_jsx(ButtonList.Item, Object.assign({ icon: "pen", onClick: () => {
                        showEditUserModal(user);
                    } }, { children: "Edit contact" })), mergeContacts && (_jsx(Tooltip, Object.assign({ content: canUserMergeValidation.reason, disabled: canUserMergeValidation.valid, dark: true, side: "left" }, { children: _jsx(ButtonList.Item, Object.assign({ icon: "combine", onClick: () => {
                            showMergeContactsModal(user);
                        }, isDisabled: !canUserMergeValidation.valid }, { children: "Merge contact" })) }))), _jsx(Link, Object.assign({ to: `/contacts/user/${user.id}` }, { children: _jsx(ButtonList.Item, Object.assign({ icon: "eye" }, { children: "View" })) })), showBanItem && !!bans.size && (_jsx(_Fragment, { children: Array.from(bans.keys()).map((channel) => {
                        const ban = bans.get(channel);
                        return ban ? (_jsx(UnbanUserButtonListItem, { user: user, channel: channel, onUnban: getActiveUserBansDelayed })) : (_jsx(BanUserButtonListItem, { user: user, channel: channel, csid: csid, ipAddress: ipAddress, onBan: getActiveUserBansDelayed }));
                    }) }))] }));
    };
    /** Gets the content element for when the component is loading. */
    const getLoadingElement = () => {
        return (_jsx(CollapseCard.Content, { children: _jsx(CollapseCard.Status, { children: "Loading..." }) }));
    };
    /** Gets the content element for when there is no contact information. */
    const getNoUserContactElement = () => {
        return (_jsx(CollapseCard.Content, { children: _jsx(CollapseCard.Status, { children: "No contact info to show" }) }));
    };
    /** Gets the content element for when there is user contact information. */
    const getUserContactElement = () => {
        var _a, _b;
        const { user } = props;
        return (_jsxs(CollapseCard.Content, { children: [(user === null || user === void 0 ? void 0 : user.phone_number) && (_jsx(ContactCardLine, { label: "Phone", address: user.phone_number, addressHref: `tel:${user.phone_number}`, addressTooltip: "Call", addressOnClick: (e) => handleMakeCall(e), addressFormat: getPhoneNumberFormat })), (_a = user === null || user === void 0 ? void 0 : user.additional_phone_numbers) === null || _a === void 0 ? void 0 : _a.map((additionalPhoneNumber) => (_jsx(ContactCardLine, { label: "Phone", address: additionalPhoneNumber, addressHref: `tel:${additionalPhoneNumber}`, addressTooltip: "Call", addressOnClick: (e) => handleMakeCall(e, additionalPhoneNumber), addressFormat: getPhoneNumberFormat }, `additional-phone-number-${additionalPhoneNumber}`))), (user === null || user === void 0 ? void 0 : user.email) && (_jsx(ContactCardLine, { label: "Email", address: user.email, addressHref: `mailto:${user.email}`, addressTooltip: "Compose email", addressOnClick: (e) => handleEmailOnClick(e) })), (_b = user === null || user === void 0 ? void 0 : user.additional_emails) === null || _b === void 0 ? void 0 : _b.map((additionalEmail) => (_jsx(ContactCardLine, { label: "Email", address: additionalEmail, addressHref: `mailto:${additionalEmail}`, addressTooltip: "Compose email", addressOnClick: (e) => handleEmailOnClick(e, additionalEmail) }, `additional-email-${additionalEmail}`))), (user === null || user === void 0 ? void 0 : user.external_id) && (_jsx(ContactCardLine, { label: "External ID", address: user.external_id })), user && (_jsx(FlexBox, Object.assign({ margin: "200 0", justifyContent: "center" }, { children: _jsx(NavigationButton, Object.assign({ navigate: () => navigate(`/contacts/user/${user.id}`) }, { children: "View contact" })) })))] }));
    };
    const { user, isMinimized, asyncStatusConversation } = props;
    if (!user) {
        return null;
    }
    const banBadgeLabel = Array.from(bans.keys())
        .filter((channel) => !!bans.get(channel))
        .map((channel) => { var _a; return (_a = conversationChannelAttributes.get(channel)) === null || _a === void 0 ? void 0 : _a.prettyName; })
        .join(', ');
    return isMinimized ? (_jsx("div", Object.assign({ className: "icon-holder contact" }, { children: getAvatarElement() }))) : (_jsxs(CollapseCard, Object.assign({ isOpen: props.isOpen, onToggle: props.onToggle }, { children: [_jsxs(CollapseCard.Header, Object.assign({ avatar: getAvatarElement(), dropdown: getMenuElement(user), onDropdownToggle: (isOpen) => {
                    if (isOpen) {
                        analytics.contactsCardActionsMenuOpened();
                    }
                } }, { children: [_jsx("span", Object.assign({ style: { whiteSpace: 'normal' } }, { children: (_b = (user.name || user.email || user.phone_number)) !== null && _b !== void 0 ? _b : '' })), !!banBadgeLabel && (_jsx("span", { children: _jsxs(Badge, Object.assign({ variant: "error", size: "small" }, { children: ["Banned on ", banBadgeLabel] })) }))] })), asyncStatusConversation === 'loading' && getLoadingElement(), asyncStatusConversation !== 'loading' &&
                !user.phone_number &&
                !user.email
                ? getNoUserContactElement()
                : getUserContactElement()] })));
};
const mapStateToProps = (state) => {
    return {
        user: state.conversation.metaConversation.requester,
        conversation: state.conversation.metaConversation.conversation,
        asyncStatusConversation: state.conversation.asyncStatus.getConversation,
        telephonyFromNumber: state.telephony.selectedFromNumber,
    };
};
const mapDispatchToProps = (dispatch) => {
    return {
        showEditUserModal: (user) => dispatch(userModalActions.show({ user })),
        showMergeContactsModal: (user) => dispatch(appActions.showModal({
            modal: {
                type: 'app:mergeContacts',
                arg: { user: transformUserToApiUser(user) },
            },
        })),
        showComposeEmailModal: (payload) => dispatch(composeEmailActions.composeEmailOpenModal(payload)),
        telephonyMakeCall: bindActionToPromise(dispatch, twilioActions.twilioMakeCall),
    };
};
export default connect(mapStateToProps, mapDispatchToProps)(ContactCard);
